import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";

export const NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH = 200;

export enum NetworkConnectionOrderField {
  Name = "Name",
  DateConnected = "DateConnected",
}

type NetworkConnectionQueryValues = {
  paginationParameters?: PaginationParameters;
  orderBy?: NetworkConnectionOrderField;
  orderDir?: OrderDirection;
  query?: string;
};

/**
 * The arguments of `GET users/network-connections`: one page of the caller's connections,
 * searched and ordered by the server. The server orders by name, ascending, when no order is sent.
 */
export default class NetworkConnectionQuery {
  public readonly paginationParameters: PaginationParameters;
  public readonly orderBy?: NetworkConnectionOrderField;
  public readonly orderDir?: OrderDirection;
  public readonly query?: string;

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([...this.paginationParameters.searchParams, ...this.cacheParams]);
  }

  /** Every argument except the page, so the pages of one search share one key. */
  public get cacheKey(): string {
    return new URLSearchParams(this.cacheParams).toString();
  }

  public constructor(values: NetworkConnectionQueryValues = {}) {
    const paginationParameters = values.paginationParameters ?? PaginationParameters.DEFAULT;
    if (
      !(paginationParameters instanceof PaginationParameters) ||
      !Number.isInteger(paginationParameters.pageIndex) || paginationParameters.pageIndex < 0 ||
      !Number.isInteger(paginationParameters.pageSize) || paginationParameters.pageSize < 1 ||
      paginationParameters.pageSize > 100
    ) {
      throw new Error("Valid pagination parameters are required.");
    }
    if (values.orderBy && !Object.values(NetworkConnectionOrderField).includes(values.orderBy)) {
      throw new Error("A valid network connection order field is required.");
    }
    if (values.orderDir && !Object.values(OrderDirection).includes(values.orderDir)) {
      throw new Error("A valid order direction is required.");
    }
    const query = values.query?.trim();
    if (query && query.length > NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH) {
      throw new Error(`query cannot exceed ${NETWORK_CONNECTION_QUERY_MAXIMUM_LENGTH} characters.`);
    }

    this.paginationParameters = paginationParameters;
    this.orderBy = values.orderBy;
    this.orderDir = values.orderDir;
    this.query = query || undefined;
  }

  public withPagination(paginationParameters: PaginationParameters): NetworkConnectionQuery {
    return new NetworkConnectionQuery({
      paginationParameters,
      orderBy: this.orderBy,
      orderDir: this.orderDir,
      query: this.query,
    });
  }

  public isEqualTo(other?: NetworkConnectionQuery): boolean {
    if (!other) return false;
    return this.cacheKey === other.cacheKey && this.paginationParameters.isEqualTo(other.paginationParameters);
  }

  private get cacheParams(): string[][] {
    return [
      ...(this.orderBy ? [["orderBy", this.orderBy]] : []),
      ...(this.orderDir ? [["orderDir", this.orderDir]] : []),
      ...(this.query ? [["query", this.query]] : []),
    ];
  }
}
